import { useEffect, useRef, useState } from "react";
import { Modal, BlockStack, InlineStack, Text, Badge, TextField, Button, Checkbox, Banner } from "@shopify/polaris";
import supabase from "../../supabase-client";

// Merchants that invoice by email: facturador-svc's AI asks for the CFDI from
// gastos@gomove.mx and follows up on its own (protocol email_request). The
// thread is mirrored into invoice_threads / invoice_messages and shown here
// live; a person can write in the thread (sent from gastos@ by the robot),
// take it from the AI or give it back. Actions go through gastos_inbox/mail.

export const THREAD_STATUS = {
  sending: { tone: "info", label: "Correo: enviando solicitud" },
  waiting: { tone: "info", label: "Correo: esperando al comercio" },
  replied: { tone: "info", label: "Correo: la IA está respondiendo" },
  needs_human: { tone: "critical", label: "Correo: necesita a alguien" },
  done: { tone: "success", label: "Correo: factura recibida" },
  failed: { tone: "critical", label: "Correo: sin factura" },
  cancelled: { tone: undefined, label: "Correo: cancelado" },
};

const when = (d) =>
  d ? new Date(d).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

// One line under the badge: what happens next (or why it stopped).
export function threadLine(th) {
  if (!th) return null;
  if (["needs_human", "failed"].includes(th.status)) return th.status_note;
  if (th.status === "waiting" && th.next_followup_at) return `Siguiente seguimiento ${when(th.next_followup_at)}`;
  return null;
}

const AUTHOR = { ai: "IA · GoMove", human: "GoMove", merchant: "Comercio", system: "" };

export function MailThreadModal({ ticketId, open, onClose, canEdit }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [attachCsf, setAttachCsf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const bottom = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let channel = null;
    let cancelled = false;
    (async () => {
      const { data: th } = await supabase.from("invoice_threads").select("*").eq("ticket_id", ticketId).maybeSingle();
      if (cancelled || !th) return;
      setThread(th);
      const { data: ms } = await supabase.from("invoice_messages").select("*").eq("thread_id", th.id).order("sent_at");
      if (!cancelled) setMessages(ms || []);
      // Live: new messages and status changes as the robot mirrors them.
      channel = supabase
        .channel(`invoice-thread-${th.id}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "invoice_messages", filter: `thread_id=eq.${th.id}` }, (p) => {
          if (!p.new?.id) return;
          setMessages((prev) =>
            [...prev.filter((m) => m.id !== p.new.id), p.new].sort((a, b) => String(a.sent_at).localeCompare(String(b.sent_at))),
          );
        })
        .on("postgres_changes", { event: "*", schema: "public", table: "invoice_threads", filter: `id=eq.${th.id}` }, (p) => {
          if (p.new?.id) setThread(p.new);
        })
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [open, ticketId]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const act = async (body) => {
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.functions.invoke("gastos_inbox/mail", { body: { ticket_id: ticketId, ...body } });
    setBusy(false);
    if (err) {
      const detail = await err.context?.json?.().catch(() => null);
      setError(detail?.error || err.message);
      return false;
    }
    return true;
  };
  const send = async () => {
    if (await act({ action: "reply", body: draft, attach: attachCsf ? ["csf"] : [] })) {
      setDraft("");
      setAttachCsf(false);
    }
  };

  const st = thread ? THREAD_STATUS[thread.status] || { label: thread.status } : null;
  const aiOwns = thread && ["sending", "waiting", "replied"].includes(thread.status);
  const closed = thread && ["done", "cancelled"].includes(thread.status);

  return (
    <Modal open={open} onClose={onClose} title={thread ? `Conversación con ${thread.merchant_email}` : "Conversación"} size="large">
      <Modal.Section>
        {!thread ? (
          <Text tone="subdued">Cargando…</Text>
        ) : (
          <BlockStack gap="300">
            <InlineStack gap="200" blockAlign="center" align="space-between">
              <InlineStack gap="200" blockAlign="center">
                <Badge tone={st.tone}>{st.label}</Badge>
                {threadLine(thread) && <Text tone="subdued" variant="bodySm">{threadLine(thread)}</Text>}
              </InlineStack>
              {canEdit && !closed && (
                <Button size="slim" loading={busy} onClick={() => act({ action: aiOwns ? "pause" : "resume" })}>
                  {aiOwns ? "Tomar el hilo (pausar IA)" : "Devolver a la IA"}
                </Button>
              )}
            </InlineStack>
            <Text tone="subdued" variant="bodySm">{thread.subject}</Text>
            <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              {messages.map((m) =>
                m.author === "system" ? (
                  <div key={m.id} style={{ textAlign: "center" }}>
                    <Text tone="subdued" variant="bodySm">{`${m.body_text} · ${when(m.sent_at)}`}</Text>
                  </div>
                ) : (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: m.direction === "out" ? "flex-end" : "flex-start",
                      maxWidth: "80%",
                      background: m.direction === "out" ? "#eef4ff" : "#f4f4f4",
                      borderRadius: 8,
                      padding: "8px 12px",
                    }}
                  >
                    <Text variant="bodySm" tone="subdued">
                      {`${m.direction === "in" ? m.from_addr : AUTHOR[m.author]} · ${when(m.sent_at)}`}
                    </Text>
                    <div style={{ whiteSpace: "pre-wrap", fontSize: 13, marginTop: 4 }}>{m.body_text}</div>
                    {(m.attachments || []).length > 0 && (
                      <Text variant="bodySm" tone="subdued">{`📎 ${m.attachments.map((a) => a.filename).join(", ")}`}</Text>
                    )}
                  </div>
                ),
              )}
              <div ref={bottom} />
            </div>
            {error && <Banner tone="critical">{error}</Banner>}
            {canEdit && !closed && (
              <BlockStack gap="200">
                <TextField
                  label="Responder desde gastos@gomove.mx"
                  multiline={3}
                  autoComplete="off"
                  value={draft}
                  onChange={setDraft}
                  helpText={aiOwns ? "La IA sigue a cargo después de tu mensaje." : "La IA está pausada; tú llevas el hilo."}
                />
                <InlineStack align="space-between" blockAlign="center">
                  <Checkbox label="Adjuntar CSF" checked={attachCsf} onChange={setAttachCsf} />
                  <Button variant="primary" loading={busy} disabled={!draft.trim()} onClick={send}>
                    Enviar
                  </Button>
                </InlineStack>
              </BlockStack>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}
